/**
 * Word (Docx) 導出 — 版面對齊 PDF：多欄圖片網格、橫向/縱向
 */

import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  ImageRun,
  AlignmentType,
  Table,
  TableRow,
  TableCell,
  WidthType,
  PageOrientation,
  BorderStyle,
} from 'docx';
import { TimezoneUtils } from '../../../../utils/timezoneUtils';
import { filterGroupedMediaBySelection } from './filterExcludedMedia';
import { normalizeStatusForDisplay } from './mediaGrouping';
import {
  buildFullGroupTitle,
  buildStatusSubGroupTitle,
  ensureDocxCompatibleImage,
  getDocxLayoutMetrics,
  scaleImageForDocxCell,
  triggerBlobDownload,
} from './exportMediaShared';

const NO_BORDER = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
const TABLE_NO_BORDERS = {
  top: NO_BORDER,
  bottom: NO_BORDER,
  left: NO_BORDER,
  right: NO_BORDER,
  insideHorizontal: NO_BORDER,
  insideVertical: NO_BORDER,
};

const headingParagraph = (text, level) =>
  new Paragraph({
    text,
    heading: level,
    spacing: { after: 120 },
  });

const emptyCellParagraph = () =>
  new Paragraph({
    children: [new TextRun({ text: '' })],
  });

const buildImageCell = (buffer, width, height, cellMaxWidthPx, minImageWidthPx) => {
  const scaled = scaleImageForDocxCell(width, height, cellMaxWidthPx, minImageWidthPx);
  return new TableCell({
    children: [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [
          new ImageRun({
            data: buffer,
            transformation: {
              width: scaled.width,
              height: scaled.height,
            },
          }),
        ],
      }),
    ],
  });
};

const buildImageGridTable = (imageItems, layout) => {
  const { cols, cellMaxWidthPx, minImageWidthPx } = layout;
  const rows = [];

  for (let i = 0; i < imageItems.length; i += cols) {
    const rowItems = imageItems.slice(i, i + cols);
    const cells = [];

    for (let c = 0; c < cols; c++) {
      const item = rowItems[c];
      if (item?.buffer) {
        cells.push(
          buildImageCell(
            item.buffer,
            item.width,
            item.height,
            cellMaxWidthPx,
            minImageWidthPx
          )
        );
      } else {
        cells.push(new TableCell({ children: [emptyCellParagraph()] }));
      }
    }

    rows.push(new TableRow({ children: cells }));
  }

  if (rows.length === 0) {
    return null;
  }

  const columnWidthTwips = Math.floor((1440 * 10) / cols);

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    columnWidths: Array(cols).fill(columnWidthTwips),
    borders: TABLE_NO_BORDERS,
    rows,
  });
};

const loadSubGroupImages = async (mediaFiles) => {
  const items = [];
  for (const mediaFile of mediaFiles) {
    try {
      const { buffer, width, height } = await ensureDocxCompatibleImage(mediaFile.filePath);
      items.push({ buffer, width, height });
    } catch (error) {
      console.error(`Docx 圖片處理失敗: ${mediaFile.filePath}`, error);
    }
  }
  return items;
};

/**
 * 導出圖片畫廊為 Docx（多圖/列，對齊 PDF）
 */
export const exportGalleryToDocx = async (
  groupedMedia,
  settings = {},
  fileName = 'apps_media_reports',
  onProgress = null,
  groupingPvs = [],
  selectedByGroup = null,
  pvDefinitions = [],
  groupingEformFields = []
) => {
  const mediaForExport = selectedByGroup && Object.keys(selectedByGroup).length > 0
    ? filterGroupedMediaBySelection(groupedMedia, selectedByGroup)
    : groupedMedia;

  if (!mediaForExport?.length) {
    throw new Error('沒有可導出的媒體');
  }

  const layout = getDocxLayoutMetrics(settings);
  const pageOrientation =
    layout.orientation === 'portrait'
      ? PageOrientation.PORTRAIT
      : PageOrientation.LANDSCAPE;

  const totalSubGroups = mediaForExport.reduce(
    (sum, group) => sum + (group.subGroups?.length || 0),
    0
  );
  let processedSubGroups = 0;
  const children = [];
  const generatedAt = TimezoneUtils.formatDateTime(new Date(), 'YYYY-MM-DD HH:mm:ss');

  children.push(
    new Paragraph({
      children: [new TextRun({ text: '應用媒體報表', bold: true, size: 32 })],
      spacing: { after: 120 },
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `生成時間: ${generatedAt}`,
          size: 20,
          color: '808080',
        }),
      ],
      spacing: { after: 200 },
    })
  );

  for (let gi = 0; gi < mediaForExport.length; gi++) {
    const group = mediaForExport[gi];
    const groupTitle = buildFullGroupTitle(group, groupingPvs, pvDefinitions, groupingEformFields);
    if (groupTitle) {
      children.push(headingParagraph(groupTitle, HeadingLevel.HEADING_1));
    }

    for (const subGroup of group.subGroups || []) {
      if (onProgress) {
        const normalizedStatus = normalizeStatusForDisplay(subGroup.status);
        onProgress(
          processedSubGroups,
          totalSubGroups,
          `正在處理分組: ${subGroup.workflowExecution?.workflowName || '未知'} (${normalizedStatus})`
        );
      }

      children.push(
        headingParagraph(buildStatusSubGroupTitle(subGroup), HeadingLevel.HEADING_2)
      );

      const imageItems = await loadSubGroupImages(subGroup.mediaFiles || []);
      const gridTable = buildImageGridTable(imageItems, layout);
      if (gridTable) {
        children.push(gridTable);
      }

      children.push(new Paragraph({ text: '', spacing: { after: 160 } }));
      processedSubGroups += 1;
    }

    if (gi < mediaForExport.length - 1) {
      children.push(new Paragraph({ text: '', spacing: { after: 120 } }));
    }
  }

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            size: {
              orientation: pageOrientation,
            },
          },
        },
        children,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const today = TimezoneUtils.formatDateTime(new Date(), 'YYYY-MM-DD');
  const orientationSuffix = layout.orientation === 'portrait' ? 'portrait' : 'landscape';
  const docxFileName = `${fileName}_${orientationSuffix}_${today}.docx`;
  triggerBlobDownload(blob, docxFileName);

  if (onProgress) {
    onProgress(totalSubGroups, totalSubGroups, '導出完成');
  }

  return { success: true, fileName: docxFileName };
};

export default {
  exportGalleryToDocx,
};
